<template>
  <v-container fluid>

    <!-- Header -->
    <div class="d-flex align-center mb-4" style="gap:12px">
      <v-btn small outlined color="info" tile @click="$router.back()">
        <v-icon left>mdi-arrow-left-bold</v-icon>Volver
      </v-btn>
      <div>
        <div class="text-h6 font-weight-bold">Solicitudes de Cambio de Programa</div>
        <div class="caption grey--text">Revisión y aprobación de transferencias de inscripción</div>
      </div>
      <v-spacer />
      <v-btn small outlined :loading="loading" @click="fetchSolicitudes" title="Actualizar">
        <v-icon small>mdi-refresh</v-icon>
      </v-btn>
    </div>

    <!-- Filtro por estado -->
    <div class="d-flex align-center mb-3 flex-wrap" style="gap:8px">
      <span class="caption grey--text font-weight-medium">Filtrar por estado:</span>
      <v-btn-toggle v-model="filtroEstado" mandatory dense rounded color="purple darken-2" @change="fetchSolicitudes">
        <v-btn value="" x-small>Todos</v-btn>
        <v-btn value="SOLICITADO" x-small>
          <v-icon left x-small>mdi-clock-outline</v-icon>Solicitado
        </v-btn>
        <v-btn value="APROBADO" x-small>
          <v-icon left x-small>mdi-check-circle-outline</v-icon>Aprobado
        </v-btn>
        <v-btn value="RECHAZADO" x-small>
          <v-icon left x-small>mdi-close-circle-outline</v-icon>Rechazado
        </v-btn>
      </v-btn-toggle>
      <v-chip v-if="total > 0" x-small color="purple darken-3" dark class="ml-2">
        {{ total }} registro(s)
      </v-chip>
    </div>

    <v-card outlined>
      <v-progress-linear v-if="loading" indeterminate color="purple darken-3" />

      <v-data-table
        :headers="headers"
        :items="solicitudes"
        :loading="loading"
        :server-items-length="total"
        :options.sync="options"
        :footer-props="{ itemsPerPageOptions: [10, 20, 50] }"
        hide-default-footer
        dense
      >
        <!-- Inscripción + Estudiante -->
        <template v-slot:item.inscripcion_origen_id="{ item }">
          <v-chip x-small color="blue darken-3" dark label class="mb-1">#{{ item.inscripcion_origen_id }}</v-chip>
          <div class="caption font-weight-medium" style="white-space:nowrap">
            {{ item.estudiante_nombres }} {{ item.estudiante_primer_apellido }} {{ item.estudiante_segundo_apellido }}
          </div>
        </template>

        <!-- Programas + Sedes -->
        <template v-slot:item.programas="{ item }">
          <div class="py-1" style="min-width:340px">
            <!-- Origen -->
            <div class="d-flex align-start mb-1" style="gap:4px">
              <v-chip x-small label outlined color="grey darken-2" style="flex-shrink:0">#{{ item.programa_origen_id }}</v-chip>
              <div>
                <span class="caption font-weight-medium grey--text text--darken-2">{{ item.programa_origen_codigo }}</span>
                <div class="caption grey--text" style="line-height:1.2;white-space:normal">{{ item.programa_origen_nombre_compuesto }}</div>
                <div class="caption blue-grey--text" style="line-height:1.2">
                  <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>{{ item.sede_origen_nombre }}
                </div>
              </div>
            </div>
            <!-- Flecha -->
            <div class="ml-1 mb-1"><v-icon x-small color="purple darken-2">mdi-arrow-down</v-icon></div>
            <!-- Destino -->
            <div class="d-flex align-start" style="gap:4px">
              <v-chip x-small label color="purple darken-2" dark style="flex-shrink:0">#{{ item.programa_destino_id }}</v-chip>
              <div>
                <span class="caption font-weight-medium purple--text text--darken-2">{{ item.programa_destino_codigo }}</span>
                <div class="caption purple--text text--darken-2" style="line-height:1.2;white-space:normal">{{ item.programa_destino_nombre_compuesto }}</div>
                <div class="caption purple--text text--darken-3" style="line-height:1.2">
                  <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>{{ item.sede_destino_nombre }}
                </div>
              </div>
            </div>
          </div>
        </template>

        <!-- Descripción -->
        <template v-slot:item.descripcion="{ item }">
          <div class="caption" style="max-width:180px;white-space:normal;line-height:1.3">{{ item.descripcion }}</div>
        </template>

        <!-- Solicitante + fecha -->
        <template v-slot:item.solicitante="{ item }">
          <div class="caption font-weight-medium" style="white-space:nowrap">
            {{ item.solicitante_nombres }} {{ item.solicitante_primer_apellido }} {{ item.solicitante_segundo_apellido }}
          </div>
          <div class="caption grey--text">{{ item.fecha_registro }}</div>
        </template>

        <!-- Estado -->
        <template v-slot:item.estado="{ item }">
          <v-chip x-small :color="colorEstado(item.estado)" dark>{{ item.estado }}</v-chip>
          <div class="caption red--text text--darken-1 mt-1" v-if="item.respuesta && item.estado === 'RECHAZADO'">
            {{ item.respuesta }}
          </div>
          <div class="caption green--text text--darken-2 mt-1" v-if="item.inscripcion_destino_id">
            Ins. destino #{{ item.inscripcion_destino_id }}
          </div>
        </template>

        <!-- Acciones -->
        <template v-slot:item.acciones="{ item }">
          <template v-if="item.estado === 'SOLICITADO'">
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small color="green darken-1" v-on="on" @click="abrirAprobar(item)">
                  <v-icon small>mdi-check-circle</v-icon>
                </v-btn>
              </template>
              <span>Aprobar</span>
            </v-tooltip>
            <v-tooltip bottom>
              <template v-slot:activator="{ on }">
                <v-btn icon small color="red darken-1" v-on="on" @click="abrirRechazar(item)">
                  <v-icon small>mdi-close-circle</v-icon>
                </v-btn>
              </template>
              <span>Rechazar</span>
            </v-tooltip>
          </template>
          <span v-else class="caption grey--text">—</span>
        </template>

        <!-- Sin datos -->
        <template v-slot:no-data>
          <div class="py-8 text-center grey--text">
            <v-icon large color="grey lighten-2">mdi-swap-horizontal</v-icon>
            <div class="mt-2 caption">Sin solicitudes{{ filtroEstado ? ' en estado ' + filtroEstado : '' }}.</div>
          </div>
        </template>
      </v-data-table>

      <!-- Paginación -->
      <div v-if="total > 0" class="d-flex align-center justify-end pa-3" style="gap:8px;border-top:1px solid #e0e0e0">
        <span class="caption grey--text">
          {{ (options.page - 1) * options.itemsPerPage + 1 }}–{{ Math.min(options.page * options.itemsPerPage, total) }} de {{ total }}
        </span>
        <v-btn icon x-small :disabled="options.page <= 1" @click="options.page--">
          <v-icon small>mdi-chevron-left</v-icon>
        </v-btn>
        <v-btn icon x-small :disabled="options.page * options.itemsPerPage >= total" @click="options.page++">
          <v-icon small>mdi-chevron-right</v-icon>
        </v-btn>
      </div>
    </v-card>

    <!-- ── Dialog rechazar ── -->
    <v-dialog v-model="dialogRechazar" max-width="420" persistent>
      <v-card>
        <v-card-title class="py-4 px-5">
          <v-icon color="red darken-1" class="mr-2">mdi-close-circle-outline</v-icon>
          <span class="subtitle-1 font-weight-bold">Rechazar solicitud</span>
        </v-card-title>
        <v-divider />
        <v-card-text class="pt-4 px-5 pb-1" v-if="solicitudGestion">
          <div class="pa-2 rounded mb-3" style="background:#f5f5f5;border-left:3px solid #bdbdbd">
            <div class="caption font-weight-bold">
              {{ solicitudGestion.estudiante_nombres }} {{ solicitudGestion.estudiante_primer_apellido }} {{ solicitudGestion.estudiante_segundo_apellido }}
            </div>
            <div class="caption grey--text">Ins. <strong>#{{ solicitudGestion.inscripcion_origen_id }}</strong></div>
            <div class="caption mt-1">
              <span class="grey--text">#{{ solicitudGestion.programa_origen_id }} {{ solicitudGestion.programa_origen_codigo }}</span>
              <v-icon x-small class="mx-1" color="purple darken-2">mdi-arrow-right</v-icon>
              <span class="purple--text text--darken-2">#{{ solicitudGestion.programa_destino_id }} {{ solicitudGestion.programa_destino_codigo }}</span>
            </div>
            <div class="caption grey--text" style="line-height:1.2;white-space:normal">
              {{ solicitudGestion.programa_destino_nombre_compuesto }}
            </div>
            <div class="caption blue-grey--text mt-1">
              <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>{{ solicitudGestion.sede_destino_nombre }}
            </div>
          </div>
          <v-textarea
            v-model="motivoRechazo"
            label="Motivo del rechazo *"
            outlined dense rows="3"
            placeholder="Indica el motivo..."
          />
        </v-card-text>
        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogRechazar = false" :disabled="procesando !== null">Cancelar</v-btn>
          <v-btn color="red darken-1" dark small
            :loading="procesando !== null"
            :disabled="!motivoRechazo"
            @click="rechazar"
          >
            <v-icon left small>mdi-close</v-icon>Rechazar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- ── Dialog aprobar (con preview) ── -->
    <v-dialog v-model="dialogAprobar" max-width="1000" persistent scrollable>
      <v-card>
        <v-toolbar color="green darken-1" dark dense flat>
          <v-icon class="mr-2">mdi-check-circle-outline</v-icon>
          <v-toolbar-title class="subtitle-1 font-weight-bold">Aprobar cambio de programa</v-toolbar-title>
          <v-spacer />
          <v-chip v-if="solicitudGestion" x-small outlined dark class="mr-2">
            Ins. #{{ solicitudGestion.inscripcion_origen_id }}
          </v-chip>
        </v-toolbar>

        <v-progress-linear v-if="loadingPlanOrigen" indeterminate color="blue darken-2" />

        <v-card-text class="pa-4" v-if="solicitudGestion">

          <!-- Justificación -->
          <div v-if="solicitudGestion.descripcion" class="caption grey--text mb-3 pa-2 rounded" style="background:#f5f5f5;border-left:3px solid #bdbdbd">
            <v-icon x-small class="mr-1">mdi-text</v-icon>{{ solicitudGestion.descripcion }}
          </div>

          <!-- Programas origen → destino -->
          <v-row dense class="mb-3">
            <!-- ORIGEN -->
            <v-col cols="12" sm="6">
              <v-card outlined style="border-color:#1565c0">
                <v-card-title class="caption font-weight-bold py-2 px-3" style="background:#e3f2fd">
                  <v-icon small class="mr-1" color="blue darken-3">mdi-school</v-icon>
                  <span class="blue--text text--darken-3">Programa Origen</span>
                </v-card-title>
                <v-card-text class="py-2 px-3">
                  <div class="caption font-weight-bold">
                    {{ solicitudGestion.estudiante_nombres }} {{ solicitudGestion.estudiante_primer_apellido }} {{ solicitudGestion.estudiante_segundo_apellido }}
                  </div>
                  <div class="caption grey--text">Ins. <strong>#{{ solicitudGestion.inscripcion_origen_id }}</strong></div>
                  <v-divider class="my-1" />
                  <div class="caption font-weight-bold blue--text text--darken-3">
                    #{{ solicitudGestion.programa_origen_id }} — {{ solicitudGestion.programa_origen_codigo }}
                  </div>
                  <div class="caption grey--text" style="line-height:1.3">
                    {{ solicitudGestion.programa_origen_nombre_compuesto }}
                  </div>
                  <div class="caption blue-grey--text mt-1">
                    <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>{{ solicitudGestion.sede_origen_nombre }}
                  </div>

                  <!-- Plan cobro con el que se inscribió -->
                  <template v-if="planCobroOrigen">
                    <v-divider class="my-2" />
                    <div class="caption font-weight-bold orange--text text--darken-2 mb-1">
                      <v-icon x-small color="orange darken-2" class="mr-1">mdi-cash-register</v-icon>
                      PLAN COBRO: {{ planCobroOrigen.nombre.toUpperCase() }}
                    </div>
                    <table style="width:100%;border-collapse:collapse;font-size:11px">
                      <thead>
                        <tr style="background:#ff6f00;color:white">
                          <th class="px-2 py-1 text-left">Concepto</th>
                          <th class="px-2 py-1 text-right">Monto (Bs.)</th>
                          <th class="px-2 py-1 text-center">Cuotas</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr v-for="(c, i) in planCobroOrigen.cobros_programa" :key="c.id || i"
                          :style="i % 2 === 0 ? 'background:#fff8e1' : 'background:white'"
                        >
                          <td class="px-2 py-1">{{ c.concepto_pago ? c.concepto_pago.nombre : '—' }}</td>
                          <td class="px-2 py-1 text-right font-weight-bold">{{ c.monto }}</td>
                          <td class="px-2 py-1 text-center">{{ c.cuotas }}</td>
                        </tr>
                      </tbody>
                    </table>
                  </template>
                  <div v-else-if="!loadingPlanOrigen" class="caption grey--text mt-1">Sin plan cobro registrado.</div>

                  <!-- Promoción aplicada -->
                  <template v-if="promoAplicada">
                    <v-divider class="my-2" />
                    <v-alert dense text color="orange darken-2" class="mb-2 pa-2" icon="mdi-tag-outline">
                      <div class="caption font-weight-bold orange--text text--darken-2">{{ promoAplicada.nombre }}</div>
                      <div class="caption grey--text text--darken-1">{{ promoAplicada.descripcion }}</div>
                      <div v-for="(d, i) in promoAplicada.detalle_promocion" :key="i" class="caption mt-1">
                        <v-icon x-small color="orange darken-2" class="mr-1">mdi-percent</v-icon>
                        <strong>{{ d.nombre_concepto }}:</strong>
                        {{ d.valor_descuento }}{{ d.is_porcentaje ? '%' : ' Bs.' }} de descuento
                      </div>
                    </v-alert>
                  </template>
                  <div v-else-if="!loadingPlanOrigen" class="caption grey--text mt-1">Sin promoción aplicada.</div>
                </v-card-text>
              </v-card>
            </v-col>

            <!-- DESTINO -->
            <v-col cols="12" sm="6">
              <v-card outlined style="border-color:#7b1fa2">
                <v-card-title class="caption font-weight-bold py-2 px-3" style="background:#f3e5f5">
                  <v-icon small class="mr-1" color="purple darken-2">mdi-school</v-icon>
                  <span class="purple--text text--darken-2">Programa Destino</span>
                </v-card-title>
                <v-card-text class="py-2 px-3">
                  <div class="caption font-weight-bold purple--text text--darken-2">
                    #{{ solicitudGestion.programa_destino_id }} — {{ solicitudGestion.programa_destino_codigo }}
                  </div>
                  <div class="caption purple--text text--darken-2" style="line-height:1.3">
                    {{ solicitudGestion.programa_destino_nombre_compuesto }}
                  </div>
                  <div class="caption purple--text text--darken-3 mt-1">
                    <v-icon x-small class="mr-1">mdi-map-marker-outline</v-icon>{{ solicitudGestion.sede_destino_nombre }}
                  </div>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Plan de pagos actual (kardex) -->
          <div class="subtitle-2 font-weight-bold mb-2 blue--text text--darken-3">
            <v-icon small color="blue darken-3" class="mr-1">mdi-receipt</v-icon>Plan de Pagos Actual del Estudiante
          </div>
          <div v-if="loadingPlanOrigen" class="caption grey--text mb-3">Cargando...</div>
          <div v-else-if="planPagoOrigen.length === 0" class="caption grey--text mb-3">Sin cuotas registradas.</div>
          <div v-else class="mb-4" style="overflow-x:auto">
            <table style="width:100%;border-collapse:collapse;font-size:12px;min-width:780px">
              <thead>
                <tr style="background:#1565c0;color:white">
                  <th class="px-2 py-1 text-left">Concepto</th>
                  <th class="px-2 py-1 text-center">N°Cuota</th>
                  <th class="px-2 py-1 text-right">Total</th>
                  <th class="px-2 py-1 text-right">M. Pagado</th>
                  <th class="px-2 py-1 text-right">M. Descuento</th>
                  <th class="px-2 py-1 text-right">M. Regularizado</th>
                  <th class="px-2 py-1 text-right">M. Compensacion</th>
                  <th class="px-2 py-1 text-right">M. Liquido</th>
                  <th class="px-2 py-1 text-right">Saldo</th>
                  <th class="px-2 py-1 text-left">Fecha Venc.</th>
                </tr>
              </thead>
              <tbody>
                <tr v-for="(c, i) in planPagoOrigen" :key="c.id || i"
                  :style="i % 2 === 0 ? 'background:#f5f5f5' : 'background:white'"
                >
                  <td class="px-2 py-1">{{ c.concepto_pago && c.concepto_pago.nombre ? c.concepto_pago.nombre : c.concepto_pago }}</td>
                  <td class="px-2 py-1 text-center">{{ c.nro_cuota }}</td>
                  <td class="px-2 py-1 text-right font-weight-medium">{{ c.total || c.monto }}</td>
                  <td class="px-2 py-1 text-right" style="color:#2e7d32">{{ c.monto_pagado }}</td>
                  <td class="px-2 py-1 text-right" :style="Number(c.monto_descuento) > 0 ? 'color:#e65100;font-weight:600' : ''">{{ c.monto_descuento }}</td>
                  <td class="px-2 py-1 text-right" :style="Number(c.monto_regularizado) > 0 ? 'color:#6a1b9a;font-weight:600' : ''">{{ c.monto_regularizado }}</td>
                  <td class="px-2 py-1 text-right" :style="Number(c.monto_compensacion) > 0 ? 'color:#1565c0;font-weight:600' : ''">{{ c.monto_compensacion }}</td>
                  <td class="px-2 py-1 text-right">{{ c.monto_liquido }}</td>
                  <td class="px-2 py-1 text-right font-weight-medium" :style="Number(c.saldo) > 0 ? 'color:#e65100' : 'color:#2e7d32'">{{ c.saldo }}</td>
                  <td class="px-2 py-1">{{ c.fecha_vencimiento || c.fecha_pago }}</td>
                </tr>
              </tbody>
            </table>
          </div>

          <v-divider class="mb-4" />

          <!-- Planes del programa destino -->
          <div class="subtitle-2 font-weight-bold mb-2 purple--text text--darken-2">
            <v-icon small color="purple darken-2" class="mr-1">mdi-cash-multiple</v-icon>
            Planes de cobro disponibles — Programa Destino
            <span class="caption font-weight-regular grey--text ml-2">(selecciona uno)</span>
          </div>
          <div v-if="loadingPrograma" class="caption grey--text mb-3">Cargando planes...</div>
          <div v-else-if="planesDestino.length === 0" class="caption grey--text mb-3">Sin planes disponibles.</div>
          <v-row v-else dense class="mb-3">
            <v-col
              v-for="plan in planesDestino"
              :key="plan.plan_cobro_programa_id || plan.id"
              cols="12" sm="6" md="4"
            >
              <v-card
                outlined
                :style="selectedPlanId === (plan.plan_cobro_programa_id || plan.id)
                  ? 'border:2px solid #7b1fa2;background:#f3e5f5;cursor:pointer'
                  : 'cursor:pointer'"
                @click="selectedPlanId = plan.plan_cobro_programa_id || plan.id; onChangePlan(plan.plan_cobro_programa_id || plan.id)"
              >
                <v-card-title class="caption font-weight-bold py-2 px-3 d-flex align-center" style="gap:6px"
                  :style="selectedPlanId === (plan.plan_cobro_programa_id || plan.id) ? 'background:#ce93d8' : 'background:#f5f5f5'"
                >
                  <v-icon x-small :color="selectedPlanId === (plan.plan_cobro_programa_id || plan.id) ? 'white' : 'purple darken-2'">
                    {{ selectedPlanId === (plan.plan_cobro_programa_id || plan.id) ? 'mdi-radiobox-marked' : 'mdi-radiobox-blank' }}
                  </v-icon>
                  <span :class="selectedPlanId === (plan.plan_cobro_programa_id || plan.id) ? 'white--text' : 'purple--text text--darken-2'">
                    {{ plan.nombre || `Plan #${plan.plan_cobro_programa_id || plan.id}` }}
                  </span>
                </v-card-title>
                <v-card-text class="pa-0">
                  <table style="width:100%;border-collapse:collapse;font-size:11px">
                    <thead>
                      <tr style="background:#7b1fa2;color:white">
                        <th class="px-2 py-1 text-left">Concepto</th>
                        <th class="px-2 py-1 text-center">Cuotas</th>
                        <th class="px-2 py-1 text-right">Monto (Bs.)</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr
                        v-for="(c, i) in (plan.cobros || plan.cobros_programa || [])"
                        :key="c.id || i"
                        :style="i % 2 === 0 ? 'background:#fafafa' : 'background:white'"
                      >
                        <td class="px-2 py-1">{{ c.nombre || (c.concepto_pago ? c.concepto_pago.nombre : '—') }}</td>
                        <td class="px-2 py-1 text-center">{{ c.cuotas }}</td>
                        <td class="px-2 py-1 text-right font-weight-bold">{{ c.monto }}</td>
                      </tr>
                    </tbody>
                    <tfoot>
                      <tr style="background:#ede7f6">
                        <td class="px-2 py-1 caption font-weight-bold" colspan="2">Total</td>
                        <td class="px-2 py-1 text-right caption font-weight-bold purple--text text--darken-2">
                          {{ (plan.cobros || plan.cobros_programa || []).reduce((s, c) => s + Number(c.monto || 0), 0).toFixed(2) }} Bs.
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </v-card-text>
              </v-card>
            </v-col>
          </v-row>

          <!-- Preview side-by-side -->
          <v-progress-linear v-if="loadingPreview" indeterminate color="green darken-2" class="mb-2" />
          <v-row v-if="preview" class="mb-3" dense>
            <!-- Antes -->
            <v-col cols="12" sm="5">
              <div class="pa-3 rounded" style="background:#fce4ec;border-left:3px solid #e53935">
                <div class="overline" style="font-size:10px;color:#c62828">Situación actual</div>
                <div class="d-flex justify-space-between mt-1">
                  <span class="caption grey--text">Pagado</span>
                  <span class="body-2 font-weight-bold">Bs {{ preview.plan_actual.monto_pagado }}</span>
                </div>
                <div class="d-flex justify-space-between">
                  <span class="caption grey--text">Pendiente</span>
                  <span class="body-2">Bs {{ preview.plan_actual.saldo_pendiente }}</span>
                </div>
              </div>
            </v-col>
            <v-col cols="12" sm="2" class="d-flex align-center justify-center">
              <v-icon color="purple darken-2">mdi-arrow-right</v-icon>
            </v-col>
            <!-- Después -->
            <v-col cols="12" sm="5">
              <div class="pa-3 rounded" style="background:#e8f5e9;border-left:3px solid #43a047">
                <div class="overline" style="font-size:10px;color:#2e7d32">Plan propuesto</div>
                <div class="d-flex justify-space-between mt-1">
                  <span class="caption grey--text">Total plan</span>
                  <span class="body-2">Bs {{ preview.plan_propuesto.total }}</span>
                </div>
                <div class="d-flex justify-space-between align-center mt-1" style="gap:8px">
                  <span class="caption grey--text" style="white-space:nowrap">Monto compensado</span>
                  <v-text-field
                    v-model.number="montoCompensado"
                    type="number" min="0"
                    prefix="Bs"
                    dense hide-details outlined
                    style="max-width:130px"
                  />
                </div>
                <v-divider class="my-1" />
                <div class="d-flex justify-space-between">
                  <span class="caption font-weight-bold">Saldo neto</span>
                  <span class="body-2 font-weight-bold green--text text--darken-2">Bs {{ saldoNeto }}</span>
                </div>
              </div>
            </v-col>
          </v-row>

          <!-- Plan pagos editable -->
          <template v-if="planPagos.length > 0">
            <div class="caption grey--text font-weight-medium mt-3 mb-1">Plan de pagos (editable)</div>
            <v-simple-table dense>
              <template v-slot:default>
                <thead>
                  <tr>
                    <th>Concepto</th>
                    <th class="text-center">Cuota</th>
                    <th class="text-right">Original (Bs)</th>
                    <th class="text-right" style="color:#e65100">Compensado (Bs)</th>
                    <th class="text-right" style="width:140px">Monto final (Bs)</th>
                    <th class="text-center" style="width:130px">Fecha pago</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(p, i) in planPagos" :key="i"
                    :style="planPagosBase[i] && planPagosBase[i].monto !== p.monto ? 'background:#fff8e1' : ''"
                  >
                    <td class="caption">{{ p.concepto_nombre }}</td>
                    <td class="caption text-center">{{ p.nro_cuota }}</td>
                    <td class="caption text-right grey--text">
                      {{ planPagosBase[i] ? planPagosBase[i].monto.toFixed(2) : '—' }}
                    </td>
                    <td class="caption text-right font-weight-medium" style="color:#e65100">
                      <span v-if="planPagosBase[i] && planPagosBase[i].monto !== p.monto">
                        - {{ (planPagosBase[i].monto - p.monto).toFixed(2) }}
                      </span>
                      <span v-else class="grey--text">—</span>
                    </td>
                    <td>
                      <v-text-field
                        v-model.number="p.monto"
                        type="number" min="0"
                        dense hide-details outlined
                        style="max-width:120px"
                      />
                    </td>
                    <td class="text-center">
                      <v-text-field
                        :value="fechaParaInput(p.fecha_pago)"
                        type="date"
                        dense hide-details outlined
                        style="max-width:140px"
                        @input="p.fecha_pago = fechaDesdeInput($event)"
                      />
                    </td>
                  </tr>
                </tbody>
              </template>
            </v-simple-table>
          </template>
        </v-card-text>

        <v-divider />
        <v-card-actions class="px-5 py-3">
          <v-spacer />
          <v-btn text small @click="dialogAprobar = false" :disabled="procesando !== null">Cancelar</v-btn>
          <v-btn color="green darken-1" dark small
            :loading="procesando !== null"
            :disabled="!selectedPlanId || planPagos.length === 0"
            @click="confirmarAprobar"
          >
            <v-icon left small>mdi-check</v-icon>Confirmar aprobación
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Snackbar -->
    <v-snackbar v-model="snackbar.show" :color="snackbar.color" top right :timeout="4000">
      {{ snackbar.msg }}
      <template v-slot:action>
        <v-btn text small @click="snackbar.show = false">OK</v-btn>
      </template>
    </v-snackbar>

  </v-container>
</template>

<script>
import xaxios from '@/xaxios';
import gql from 'graphql-tag';

const API = process.env.VUE_APP_API_REST;

export default {
  name: 'SolicitudesCambioProgramaListado',

  data() {
    return {
      solicitudes: [],
      total: 0,
      loading: false,
      procesando: null,
      filtroEstado: 'SOLICITADO',
      options: { page: 1, itemsPerPage: 20 },

      dialogRechazar: false,
      dialogAprobar: false,
      solicitudGestion: null,
      motivoRechazo: '',

      // Aprobación
      loadingPrograma: false,
      loadingPlanOrigen: false,
      planPagoOrigen: [],
      planCobroOrigen: null,
      promoAplicada: null,
      planesDestino: [],
      selectedPlanId: null,
      loadingPreview: false,
      preview: null,
      planPagos: [],
      planPagosBase: [],
      montoCompensado: 0,
      asesorId: null,
      modulosDestino: [],

      snackbar: { show: false, msg: '', color: 'success' },

      headers: [
        { text: 'ID',         value: 'id',                   sortable: false, width: '55px' },
        { text: 'Inscripción',value: 'inscripcion_origen_id', sortable: false, width: '100px' },
        { text: 'Programas',  value: 'programas',             sortable: false },
        { text: 'Descripción',value: 'descripcion',           sortable: false },
        { text: 'Solicitante',value: 'solicitante',           sortable: false, width: '140px' },
        { text: 'Estado',     value: 'estado',                sortable: false, width: '120px' },
        { text: 'Acciones',   value: 'acciones',              sortable: false, width: '90px' },
      ],
    };
  },

  computed: {
    saldoNeto() {
      const total = parseFloat(this.preview?.plan_propuesto?.total || 0);
      const compensado = parseFloat(this.montoCompensado || 0);
      return Math.max(0, total - compensado).toFixed(2);
    },
  },

  watch: {
    'options.page'()            { this.fetchSolicitudes(); },
    'options.itemsPerPage'()    { this.options.page = 1; this.fetchSolicitudes(); },
    montoCompensado()           { this.aplicarCompensacion(); },
  },

  methods: {
    async fetchSolicitudes() {
      this.loading = true;
      try {
        const params = { page: this.options.page, per_page: this.options.itemsPerPage };
        if (this.filtroEstado) params.estado = this.filtroEstado;
        const { data } = await xaxios.get(`${API}/api/v1/cambio-programa/solicitudes`, { params });
        this.solicitudes = data.data?.data || [];
        this.total = data.data?.total || 0;
      } catch {
        this.solicitudes = [];
        this.total = 0;
      } finally {
        this.loading = false;
      }
    },

    abrirRechazar(item) {
      this.solicitudGestion = item;
      this.motivoRechazo = '';
      this.dialogRechazar = true;
    },

    async rechazar() {
      this.procesando = this.solicitudGestion.id + '_re';
      try {
        await xaxios.patch(
          `${API}/api/v1/cambio-programa/solicitudes/${this.solicitudGestion.id}`,
          { estado: 'RECHAZADO', respuesta: this.motivoRechazo }
        );
        this.notificar('Solicitud rechazada', 'warning');
        this.dialogRechazar = false;
        await this.fetchSolicitudes();
      } catch (e) {
        this.notificar(this.extractError(e, 'Error al rechazar'), 'error');
      } finally {
        this.procesando = null;
      }
    },

    async abrirAprobar(item) {
      this.solicitudGestion = item;
      this.selectedPlanId = null;
      this.preview = null;
      this.planPagos = [];
      this.planPagoOrigen = [];
      this.planCobroOrigen = null;
      this.promoAplicada = null;
      this.asesorId = null;
      this.modulosDestino = [];
      this.dialogAprobar = true;

      this.loadingPrograma = true;
      this.loadingPlanOrigen = true;

      const gqlPlan = gql`query {
        getInscripcion(id: ${item.inscripcion_origen_id}) {
          asesor { id }
          plan_programa {
            id nombre
            cobros_programa { id monto cuotas concepto_pago { id nombre } }
          }
        }
      }`;

      const [rPrograma, rPlan, rPromo, rGql] = await Promise.allSettled([
        xaxios.get(`${API}/esam/programa/id/${item.programa_destino_id}`),
        xaxios.get(`${API}/api/v1/inscripcion/${item.inscripcion_origen_id}/plan-pago`),
        xaxios.get(`${API}/api/v1/esam/promociones/${item.inscripcion_origen_id}/inscripcion`),
        this.$apollo.query({ query: gqlPlan, fetchPolicy: 'network-only' }),
      ]);

      this.loadingPrograma = false;
      this.loadingPlanOrigen = false;

      if (rPrograma.status === 'fulfilled') {
        this.planesDestino = rPrograma.value.data?.planes || [];
        this.modulosDestino = (rPrograma.value.data?.programa_modulos || [])
          .filter(m => m.numero > 0)
          .sort((a, b) => a.numero - b.numero);
      } else {
        this.planesDestino = [];
        this.modulosDestino = [];
      }
      if (rPlan.status === 'fulfilled') {
        this.planPagoOrigen = rPlan.value.data?.data || [];
      }
      if (rPromo.status === 'fulfilled') {
        const d = rPromo.value.data?.data;
        this.promoAplicada = d?.nombre ? d : null;
      }
      if (rGql.status === 'fulfilled') {
        const ins = rGql.value.data?.getInscripcion;
        this.planCobroOrigen = ins?.plan_programa || null;
        this.asesorId = ins?.asesor?.id || null;
      }

    },

    async onChangePlan(planId) {
      if (!planId || !this.solicitudGestion) return;
      this.loadingPreview = true;
      this.preview = null;
      this.planPagos = [];
      try {
        const { data } = await xaxios.get(
          `${API}/api/v1/cambio-programa/solicitudes/${this.solicitudGestion.id}/preview`,
          { params: { plan_cobro_programa_id: planId } }
        );
        this.preview = data.data;
        this.buildPlanPagos(data.data, planId);
        this.montoCompensado = parseFloat(data.data?.plan_propuesto?.monto_compensado || 0);
      } catch {
        this.preview = null;
        this.buildPlanPagosFromDef(planId);
      } finally {
        this.loadingPreview = false;
      }
    },

    // DD/MM/YYYY → YYYY-MM-DD (para el input type="date")
    fechaParaInput(ddmmyyyy) {
      if (!ddmmyyyy) return '';
      const [d, m, y] = ddmmyyyy.split('/');
      return `${y}-${m}-${d}`;
    },
    // YYYY-MM-DD (del input) → DD/MM/YYYY (para el backend)
    fechaDesdeInput(yyyymmdd) {
      if (!yyyymmdd) return null;
      const [y, m, d] = yyyymmdd.split('-');
      return `${d}/${m}/${y}`;
    },

    fechaDesdeCuota(cuotaGlobal) {
      const d = new Date();
      d.setDate(15);
      d.setMonth(d.getMonth() + cuotaGlobal);
      const dia = String(d.getDate()).padStart(2, '0');
      const mes = String(d.getMonth() + 1).padStart(2, '0');
      return `${dia}/${mes}/${d.getFullYear()}`;
    },

    aplicarCompensacion() {
      if (!this.planPagosBase.length) return;
      let restante = parseFloat(this.montoCompensado) || 0;
      this.planPagos = this.planPagosBase.map(p => {
        const base = parseFloat(p.monto);
        if (restante <= 0) return { ...p, monto: base };
        if (restante >= base) {
          restante -= base;
          return { ...p, monto: 0 };
        }
        const resultado = +(base - restante).toFixed(2);
        restante = 0;
        return { ...p, monto: resultado };
      });
    },

    buildPlanPagos(preview, planId) {
      const cobros = preview?.plan_propuesto?.cobros || [];
      const plan = this.planesDestino.find(p => (p.plan_cobro_programa_id || p.id) === planId);
      const defCobros = plan?.cobros || plan?.cobros_programa || [];

      let cuotaGlobal = 0;
      this.planPagos = cobros.flatMap((c) => {
        const cuotas = c.cuotas || 1;
        const def = defCobros.find(d => (d.id || d.cobro_programa_id) === c.cobro_programa_id);
        const nombre = def?.nombre || def?.concepto_pago?.nombre || `Concepto ${c.concepto_pago_id}`;
        return Array.from({ length: cuotas }, (_, i) => ({
          cobro_programa_id: c.cobro_programa_id,
          concepto_pago_id:  c.concepto_pago_id,
          concepto_nombre:   nombre,
          monto:             +(parseFloat(c.monto) / cuotas).toFixed(2),
          nro_cuota:         i + 1,
          fecha_pago:        this.fechaDesdeCuota(cuotaGlobal++),
        }));
      });
      this.planPagosBase = this.planPagos.map(p => ({ ...p }));
    },

    buildPlanPagosFromDef(planId) {
      const plan = this.planesDestino.find(p => (p.plan_cobro_programa_id || p.id) === planId);
      const cobros = plan?.cobros || plan?.cobros_programa || [];

      let cuotaGlobal = 0;
      this.planPagos = cobros.flatMap((c) => {
        const cuotas = c.cuotas || 1;
        const nombre = c.nombre || c.concepto_pago?.nombre || `Concepto ${c.concepto_pago_id || c.id}`;
        return Array.from({ length: cuotas }, (_, i) => ({
          cobro_programa_id: c.cobro_programa_id || c.id,
          concepto_pago_id:  c.concepto_pago_id || c.concepto_pago?.id,
          concepto_nombre:   nombre,
          monto:             +(parseFloat(c.monto || 0) / cuotas).toFixed(2),
          nro_cuota:         i + 1,
          fecha_pago:        this.fechaDesdeCuota(cuotaGlobal++),
        }));
      });
      this.planPagosBase = this.planPagos.map(p => ({ ...p }));
    },

    async confirmarAprobar() {
      this.procesando = this.solicitudGestion.id + '_ap';
      try {
        await xaxios.patch(
          `${API}/api/v1/cambio-programa/solicitudes/${this.solicitudGestion.id}`,
          {
            estado: 'APROBADO',
            plan_cobro_programa_id: this.selectedPlanId,
            asesor_id: this.asesorId,
            monto_compensado: parseFloat(this.montoCompensado) || 0,
            plan_pagos: this.planPagosBase.map((p, i) => ({
              cobro_programa_id: p.cobro_programa_id,
              concepto_pago_id:  p.concepto_pago_id,
              monto:             parseFloat(p.monto),
              nro_cuota:         p.nro_cuota,
              fecha_pago:        this.planPagos[i]?.fecha_pago || p.fecha_pago || null,
            })),
            compensaciones: this.planPagos
              .map((p, i) => {
                const base = this.planPagosBase[i];
                const monto_compensacion = base ? +(parseFloat(base.monto) - parseFloat(p.monto)).toFixed(2) : 0;
                return monto_compensacion > 0 ? {
                  cobro_programa_id: p.cobro_programa_id,
                  concepto_pago_id:  p.concepto_pago_id,
                  nro_cuota:         p.nro_cuota,
                  monto_compensacion,
                } : null;
              })
              .filter(Boolean),
          }
        );
        this.notificar('Solicitud aprobada. Transferencia procesada.', 'success');
        this.dialogAprobar = false;
        await this.fetchSolicitudes();
      } catch (e) {
        this.notificar(this.extractError(e, 'Error al aprobar'), 'error');
      } finally {
        this.procesando = null;
      }
    },

    extractError(e, fallback) {
      if (e?.response?.status === 403) return 'No tienes permiso para esta acción en la sede destino';
      return e?.response?.data?.message || e?.response?.data?.data || fallback;
    },

    colorEstado(estado) {
      const map = { SOLICITADO: 'orange darken-2', APROBADO: 'green darken-2', RECHAZADO: 'red darken-1' };
      return map[estado] || 'grey';
    },

    notificar(msg, color = 'success') {
      this.snackbar = { show: true, msg, color };
    },
  },

  mounted() {
    this.fetchSolicitudes();
  },
};
</script>
